import { authApi } from './authApi';
import { INQUIRIES_API_BASE, getApiErrorMessage, parseApiResponse } from '../config/apiConfig';

export interface CreateInquiryRequest {
  name: string;
  email: string;
  phone: string;
  companyName?: string;
  message: string;
}

export interface InquiryResponse {
  id: string;
  name: string;
  email: string;
  phone?: string;
  companyName?: string;
  message: string;
  createdDate: string;
  status: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

class InquiryApi {
  private baseUrl: string;

  constructor(baseUrl: string = INQUIRIES_API_BASE) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): HeadersInit {
    const token = authApi.getToken();

    return token
      ? {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }
      : {
          'Content-Type': 'application/json',
        };
  }

  async submitInquiry(
    request: CreateInquiryRequest
  ): Promise<{ success: boolean; data?: InquiryResponse; error?: string }> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      const data = await parseApiResponse<{ success?: boolean; data?: InquiryResponse; message?: string }>(response);
      const responseData = data as { success?: boolean; data?: InquiryResponse; message?: string } | null;

      if (!response.ok) {
        return {
          success: false,
          error: getApiErrorMessage(data, 'Failed to submit inquiry'),
        };
      }

      return {
        success: Boolean(responseData?.success ?? true),
        data: responseData?.data,
        error: responseData?.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async getInquiries(): Promise<{ success: boolean; data?: InquiryResponse[]; error?: string }> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      const data = await parseApiResponse<{ success?: boolean; data?: InquiryResponse[]; message?: string }>(response);
      const responseData = data as { success?: boolean; data?: InquiryResponse[]; message?: string } | null;

      if (!response.ok) {
        return {
          success: false,
          error: getApiErrorMessage(data, 'Failed to load inquiries'),
        };
      }

      return {
        success: Boolean(responseData?.success ?? true),
        data: responseData?.data ?? [],
        error: responseData?.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async deleteInquiry(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });

      const data = await parseApiResponse<{ success?: boolean; message?: string }>(response);

      if (!response.ok) {
        return {
          success: false,
          error: getApiErrorMessage(data, 'Failed to delete inquiry'),
        };
      }

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }
}

export const inquiryApi = new InquiryApi();

